import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';
import { Globe2, Loader2, AlertTriangle, Info, X } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import ShippyLoader from '../components/ShippyLoader';
import CarrierLogo from '../components/CarrierLogo';
import { COUNTRY_CENTROIDS, latLonToVec3 } from '../data/countryCentroids';
import { LAND } from '../data/worldLand';

const R = 2;                          // globe radius in scene units
const TEX_W = 8192, TEX_H = 4096;     // equirectangular; 0.1 degree is 2.3 px here

const flag = (iso2) => (!iso2 || iso2.length !== 2) ? ''
  : String.fromCodePoint(...iso2.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65));

const OCEAN = '#0b1424';
const LAND_UNSERVED = '#2a3446';
const COAST = '#3d4d66';
const GRATICULE = '#131e30';

// Cheap to dear. One hue path, so the eye reads "warmer is dearer" without a
// legend, and the ends are far enough apart that mainland vs island stays
// distinguishable when they sit two steps apart on the quantile scale.
const RAMP = ['#1d9bf0', '#2dd4bf', '#a3e635', '#facc15', '#fb923c', '#ef4444'];
const RAMP_RGB = RAMP.map(h => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]);

function rampAt(t) {
  const x = Math.min(1, Math.max(0, t)) * (RAMP_RGB.length - 1);
  const i = Math.min(RAMP_RGB.length - 2, Math.floor(x));
  const f = x - i;
  const a = RAMP_RGB[i], b = RAMP_RGB[i + 1];
  return `rgb(${Math.round(a[0] + (b[0] - a[0]) * f)},${Math.round(a[1] + (b[1] - a[1]) * f)},${Math.round(a[2] + (b[2] - a[2]) * f)})`;
}

/** Quantile scale over every price the carrier has, countries and zones alike.
 *
 *  Linear would let one intercontinental lane at EUR 300 crush the whole of
 *  Europe into the first colour and the Balearics would look like Madrid.
 *  Ranking spreads the colours over the prices that actually exist, which is
 *  what makes a zone visibly different from the country around it.
 */
function makeScale(values) {
  const v = [...new Set(values.filter(x => x != null))].sort((a, b) => a - b);
  if (v.length < 2) return { t: () => 0.5, ticks: v, n: v.length };
  const pos = new Map(v.map((x, i) => [x, i / (v.length - 1)]));
  return {
    t: (x) => pos.get(x) ?? 0.5,
    ticks: [v[0], v[Math.floor(v.length / 4)], v[Math.floor(v.length / 2)], v[Math.floor(3 * v.length / 4)], v[v.length - 1]],
    n: v.length,
  };
}

const px = (lon) => (lon + 180) / 360 * TEX_W;
const py = (lat) => (90 - lat) / 180 * TEX_H;

/** Paint one closed ring, unwrapping it across the antimeridian if it spans it. */
function paintRing(ctx, ring) {
  let min = 180, max = -180;
  for (let i = 0; i < ring.length; i += 2) { if (ring[i] < min) min = ring[i]; if (ring[i] > max) max = ring[i]; }
  const wraps = max - min > 180;
  for (const shift of wraps ? [0, -360] : [0]) {
    ctx.beginPath();
    for (let i = 0; i < ring.length; i += 2) {
      let lon = ring[i];
      if (wraps && lon < 0) lon += 360;
      const x = px(lon + shift), y = py(ring[i + 1]);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}

/** The whole picture as one texture: ocean, land coloured by country price,
 *  zones painted over their country as patches coloured by their own price.
 *
 *  A texture rather than geometry because a FILL is what was asked for, and a
 *  zone's shape is thousands of little cells that would be thousands of meshes
 *  otherwise. Repainted only when the carrier or the focused zone changes.
 */
function buildTexture(data, scale, focusId) {
  const cv = document.createElement('canvas');
  cv.width = TEX_W; cv.height = TEX_H;
  const ctx = cv.getContext('2d');
  ctx.fillStyle = OCEAN;
  ctx.fillRect(0, 0, TEX_W, TEX_H);

  ctx.strokeStyle = GRATICULE; ctx.lineWidth = 2;
  for (let lat = -60; lat <= 60; lat += 30) { ctx.beginPath(); ctx.moveTo(0, py(lat)); ctx.lineTo(TEX_W, py(lat)); ctx.stroke(); }
  for (let lon = -150; lon <= 150; lon += 30) { ctx.beginPath(); ctx.moveTo(px(lon), 0); ctx.lineTo(px(lon), TEX_H); ctx.stroke(); }

  const byIso = new Map((data?.countries || []).map(c => [c.iso2, c]));
  ctx.lineWidth = 3; ctx.lineJoin = 'round'; ctx.strokeStyle = COAST;
  for (const [iso, ring] of LAND) {
    const c = byIso.get(iso);
    ctx.fillStyle = c && c.served ? rampAt(scale.t(c.cost)) : LAND_UNSERVED;
    paintRing(ctx, ring);
  }

  // Zones over their countries. Replacement zones first (they define the
  // base there), override zones on top. A focused zone keeps full strength
  // while the others fade, so it can be found on a busy country.
  const prec = data?.precision || 0.1;
  const side = prec * 1.45;             // a little overlap, so adjacent cells read as one patch
  const zones = [...(data?.zones || [])].sort((a, b) => (a.kind === 'replacement' ? 0 : 1) - (b.kind === 'replacement' ? 0 : 1));
  for (const z of zones) {
    if (!z.cells?.length) continue;
    const dim = focusId != null && z.id !== focusId;
    ctx.globalAlpha = dim ? 0.28 : 1;
    ctx.fillStyle = z.served ? rampAt(scale.t(z.cost)) : '#111827';
    for (const [lat, lon] of z.cells) {
      const w = px(lon + side / 2) - px(lon - side / 2);
      const h = py(lat - side / 2) - py(lat + side / 2);
      ctx.fillRect(px(lon - side / 2), py(lat + side / 2), w, h);
    }
    if (!z.served) {
      // hatch the holes: a place the carrier does not go is a warning, not a colour
      ctx.strokeStyle = '#475569'; ctx.lineWidth = 1.5;
      for (const [lat, lon] of z.cells) {
        const x = px(lon - side / 2), y = py(lat + side / 2), w = px(lon + side / 2) - x;
        ctx.beginPath(); ctx.moveTo(x, y + w); ctx.lineTo(x + w, y); ctx.stroke();
      }
    }
    if (focusId != null && z.id === focusId) {
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 2.5;
      for (const [lat, lon] of z.cells) {
        const x = px(lon - side / 2), y = py(lat + side / 2);
        ctx.strokeRect(x, y, px(lon + side / 2) - x, py(lat - side / 2) - y);
      }
    }
  }
  ctx.globalAlpha = 1;

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** Point-in-ring on raw lon/lat, for hover. Rings are small; a scan is fine. */
function countryAt(lon, lat) {
  for (const [iso, r] of LAND) {
    let hit = false;
    for (let i = 0, j = r.length - 2; i < r.length; j = i, i += 2) {
      const xi = r[i], yi = r[i + 1], xj = r[j], yj = r[j + 1];
      if ((yi > lat) !== (yj > lat) && lon < (xj - xi) * (lat - yi) / (yj - yi) + xi) hit = !hit;
    }
    if (hit) return iso;
  }
  return null;
}

function Globe({ texture, onHover }) {
  const move = useCallback((e) => {
    if (!e.uv) return;
    // three.js SphereGeometry stores v as 1 - polar fraction, so v = 1 at the
    // north pole; the texture is drawn with north at the top, which matches.
    const lon = e.uv.x * 360 - 180;
    const lat = e.uv.y * 180 - 90;
    onHover({ lon, lat, x: e.nativeEvent.clientX, y: e.nativeEvent.clientY });
  }, [onHover]);
  return (
    <mesh onPointerMove={move} onPointerOut={() => onHover(null)}>
      <sphereGeometry args={[R, 128, 128]} />
      {/* Unlit on purpose: a light puts half the world in shadow and hides data. */}
      <meshBasicMaterial map={texture} />
    </mesh>
  );
}

export default function CoverageMap() {
  const [carriers, setCarriers] = useState([]);
  const [carrierId, setCarrierId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [focus, setFocus] = useState(null);
  const [hover, setHover] = useState(null);
  const [listHover, setListHover] = useState(null);
  const [nearest, setNearest] = useState(null);
  const mapRef = useRef(null);
  const nearestReq = useRef(0);

  useEffect(() => {
    axiosInstance.get('api/zones/overview/')
      .then(r => {
        const zoneList = r.data?.zones || [];
        const fromZones = []; const seen = new Set();
        zoneList.forEach(z => (z.carriers || []).forEach(c => {
          if (c?.id != null && !seen.has(c.id)) { seen.add(c.id); fromZones.push(c); }
        }));
        const all = (r.data?.carriers?.length ? r.data.carriers : fromZones) || [];
        const act = all.filter(c => c.is_active);
        const use = act.length ? act : all;
        setCarriers(use);
        if (use.length) setCarrierId(String(use[0].id));
        else setError(`The zones API returned no carriers (keys: ${Object.keys(r.data || {}).join(', ') || 'none'}).`);
      })
      .catch(e => setError(e?.response?.status
        ? `Could not load carriers (HTTP ${e.response.status} from api/zones/overview/).`
        : `Could not reach the zones API: ${e.message}`))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!carrierId) return;
    setBusy(true); setError(''); setFocus(null);
    axiosInstance.get('api/zones/carrier-map/', { params: { carrier_id: carrierId } })
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.status
        ? `Could not load the carrier map (HTTP ${e.response.status}).`
        : `Could not reach the zones API: ${e.message}`))
      .finally(() => setBusy(false));
  }, [carrierId]);

  const scale = useMemo(() => makeScale([
    ...(data?.countries || []).map(c => c.cost),
    ...(data?.zones || []).map(z => z.cost),
  ]), [data]);

  // Building an 8k texture takes a few hundred ms; do it only when the
  // picture actually changes, and dispose the old one or the GPU keeps it.
  const texture = useMemo(() => (data ? buildTexture(data, scale, focus) : null), [data, scale, focus]);
  useEffect(() => () => { if (texture) texture.dispose(); }, [texture]);

  // Zone under a lat/lon, by cell key: the same rounding the server binned with.
  const cellIndex = useMemo(() => {
    const m = new Map();
    const prec = data?.precision || 0.1;
    for (const z of data?.zones || []) for (const [lat, lon] of z.cells || []) {
      m.set(`${Math.round(lat / prec)}|${Math.round(lon / prec)}`, z);
    }
    return { m, prec };
  }, [data]);

  const hovered = useMemo(() => {
    if (!hover || !data) return null;
    const iso = countryAt(hover.lon, hover.lat);
    const z = cellIndex.m.get(`${Math.round(hover.lat / cellIndex.prec)}|${Math.round(hover.lon / cellIndex.prec)}`);
    const c = iso ? data.countries.find(x => x.iso2 === iso) : null;
    if (!c && !z) return null;
    return { iso: iso || z?.iso2, country: c, zone: z, lat: hover.lat, lon: hover.lon, x: hover.x, y: hover.y };
  }, [hover, data, cellIndex]);

  // The cell under the cursor back into a real postcode. Debounced so a sweep
  // across Europe is a handful of requests, and stamped so a slow answer for
  // Madrid cannot land after a fast one for Lisbon.
  useEffect(() => {
    if (!hovered?.iso) { setNearest(null); return undefined; }
    const iso = hovered.iso, lat = hovered.lat, lon = hovered.lon;
    const id = ++nearestReq.current;
    const t = setTimeout(() => {
      axiosInstance.get('api/zones/nearest/', { params: { iso2: iso, lat: lat.toFixed(3), lon: lon.toFixed(3) } })
        .then(r => { if (id === nearestReq.current) setNearest(r.data?.found ? r.data : null); })
        .catch(() => { if (id === nearestReq.current) setNearest(null); });
    }, 110);
    return () => clearTimeout(t);
  }, [hovered?.iso, hovered ? Math.round(hovered.lat * 20) : null, hovered ? Math.round(hovered.lon * 20) : null]);

  const zoneCentroid = useCallback((z) => {
    if (!z?.cells?.length) return null;
    let la = 0, lo = 0;
    for (const [a, b] of z.cells) { la += a; lo += b; }
    return [la / z.cells.length, lo / z.cells.length];
  }, []);

  const countries = useMemo(() => [...(data?.countries || [])].sort((a, b) => (a.cost ?? 1e9) - (b.cost ?? 1e9)), [data]);
  const zones = useMemo(() => [...(data?.zones || [])].sort((a, b) => (a.cost ?? 1e9) - (b.cost ?? 1e9)), [data]);
  const served = countries.filter(c => c.served).length;
  const carrier = carriers.find(c => String(c.id) === carrierId);

  // Marker for a list row being hovered, so a zone can be found on the globe.
  const marker = useMemo(() => {
    if (!listHover) return null;
    if (listHover.kind === 'country') {
      const p = COUNTRY_CENTROIDS[listHover.iso2];
      return p ? { pos: latLonToVec3(p[0], p[1], R + 0.05), label: listHover.label } : null;
    }
    const c = zoneCentroid(listHover.zone);
    return c ? { pos: latLonToVec3(c[0], c[1], R + 0.05), label: listHover.label } : null;
  }, [listHover, zoneCentroid]);

  if (loading) return <ShippyLoader />;

  const fmt = (v) => (v == null ? 'not served' : `€${Number(v).toFixed(2)}`);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Globe2 className="w-6 h-6 text-sky-600" /> Carrier coverage
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Every country filled with what this carrier charges there, and every zone painted over it
            in its own price. Drag to rotate, scroll to zoom, hover for the number.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {carrier && <CarrierLogo name={carrier.name} showName={false} height={22} />}
          <select className="h-9 rounded border px-2 text-sm min-w-[260px]"
                  value={carrierId} onChange={e => setCarrierId(e.target.value)} disabled={!carriers.length}>
            {carriers.length
              ? carriers.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)
              : <option value="">No carriers loaded</option>}
          </select>
          {busy && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4 items-start">
        <div ref={mapRef} className="rounded-lg border bg-slate-950 overflow-hidden relative" style={{ height: 600 }}>
          {texture && (
            <Canvas camera={{ position: [0.6, 1.4, 5.2], fov: 45 }}>
              <Globe texture={texture} onHover={setHover} />
              {marker && (
                <Html position={marker.pos} center style={{ pointerEvents: 'none' }}>
                  <div className="rounded-full border-2 border-white w-3.5 h-3.5 shadow-[0_0_0_2px_rgba(0,0,0,.6)]" />
                </Html>
              )}
              <OrbitControls enablePan={false} minDistance={2.6} maxDistance={9} rotateSpeed={0.5} zoomSpeed={0.8} />
            </Canvas>
          )}
          {hovered && mapRef.current && (() => {
            // Screen-space, fixed pixel size, offset from the cursor so it never
            // covers the spot being read. Flips to the left near the right edge.
            const box = mapRef.current.getBoundingClientRect();
            const x = hovered.x - box.left, y = hovered.y - box.top;
            const flip = x > box.width - 240;
            const zoneDelta = hovered.zone && hovered.zone.cost != null && hovered.country?.cost != null
              ? hovered.zone.cost - hovered.country.cost : null;
            return (
              <div className="absolute pointer-events-none z-10"
                   style={{ left: flip ? x - 14 : x + 14, top: y + 14, transform: flip ? 'translateX(-100%)' : undefined }}>
                <div className="rounded-md bg-slate-900/95 border border-slate-700 px-2 py-1 text-[11px] leading-tight text-white shadow-lg whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium">
                      {flag(hovered.iso)} {hovered.zone ? hovered.zone.name : hovered.country?.country}
                    </span>
                    {hovered.zone && (
                      <span className={`rounded px-1 text-[9px] font-medium ${hovered.zone.kind === 'override' ? 'bg-sky-800 text-sky-100' : 'bg-amber-800 text-amber-100'}`}>
                        {hovered.zone.kind}
                      </span>
                    )}
                  </div>
                  {nearest?.postal_code && (
                    <div className="text-slate-400 font-mono text-[10px]">
                      {nearest.postal_code} {nearest.place}
                    </div>
                  )}
                  <div className="tabular-nums mt-0.5">
                    <span className={hovered.zone ? (hovered.zone.served ? 'text-white' : 'text-red-300') : (hovered.country?.served ? 'text-white' : 'text-slate-400')}>
                      {hovered.zone ? fmt(hovered.zone.cost) : fmt(hovered.country?.cost)}
                    </span>
                    {hovered.zone && hovered.country?.served && (
                      <span className="text-slate-400">
                        {' '}· mainland {fmt(hovered.country.cost)}
                        {zoneDelta != null && zoneDelta !== 0 && (
                          <span className={zoneDelta > 0 ? 'text-amber-300' : 'text-emerald-300'}> ({zoneDelta > 0 ? '+' : ''}{zoneDelta.toFixed(2)})</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          {!texture && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> pricing the world…
            </div>
          )}
          {focus != null && (
            <button onClick={() => setFocus(null)}
                    className="absolute top-3 left-3 flex items-center gap-1 rounded bg-slate-900/90 px-2 py-1 text-[11px] text-white">
              <X className="w-3 h-3" /> {zones.find(z => z.id === focus)?.name}
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="text-sm font-medium mb-2">{carrier?.name}</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><div className="text-xs text-gray-500">Countries</div>
                   <div className="text-lg font-semibold">{served}</div></div>
              <div><div className="text-xs text-gray-500">Zones</div>
                   <div className="text-lg font-semibold">{zones.length}</div></div>
              <div><div className="text-xs text-gray-500">Not served</div>
                   <div className="text-lg font-semibold text-slate-500">{countries.length - served}</div></div>
            </div>
            <div className="mt-3">
              <div className="h-2.5 rounded" style={{ background: `linear-gradient(90deg, ${RAMP.join(',')})` }} />
              <div className="flex justify-between text-[11px] text-gray-500 mt-1 tabular-nums">
                {scale.ticks.map((v, i) => <span key={i}>€{Number(v).toFixed(0)}</span>)}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                Quantile scale over the {scale.n} distinct prices this carrier has, so a zone stays
                visibly different from its country even when one far lane is ten times dearer.
              </p>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500">
                <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-2.5 rounded" style={{ background: LAND_UNSERVED }} /> country not served</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-2.5 rounded" style={{ background: 'repeating-linear-gradient(45deg,#111827,#111827 2px,#475569 2px,#475569 3px)' }} /> zone not served</span>
              </div>
            </div>
            {data && (
              <p className="text-xs text-gray-400 mt-3">
                Reference parcel {data.reference_parcel.width}×{data.reference_parcel.height}×{data.reference_parcel.depth} cm, {data.reference_parcel.weight_kg} kg, priced at the country card and at one postcode inside each zone.
              </p>
            )}
          </div>

          <div className="rounded-lg border bg-white">
            <div className="px-3 py-2 border-b text-xs uppercase text-gray-500">Zones, cheapest first · click to focus</div>
            {!zones.length && <div className="px-3 py-3 text-xs text-gray-500">No zone concerns this carrier.</div>}
            <div className="max-h-64 overflow-y-auto divide-y">
              {zones.map(z => (
                <button key={z.id}
                        onClick={() => setFocus(focus === z.id ? null : z.id)}
                        onMouseEnter={() => setListHover({ kind: 'zone', zone: z, label: z.name })}
                        onMouseLeave={() => setListHover(null)}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left hover:bg-gray-50 ${focus === z.id ? 'bg-sky-50' : ''}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="inline-block w-3 h-3 rounded-sm shrink-0"
                          style={{ background: z.served ? rampAt(scale.t(z.cost)) : '#111827' }} />
                    <span className="truncate">{flag(z.iso2)} {z.name}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    {!z.cells?.length && <span className="text-[10px] text-gray-400" title="no postcode coordinates for this country">list only</span>}
                    <span className="tabular-nums text-xs">{fmt(z.cost)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border bg-white">
            <div className="px-3 py-2 border-b text-xs uppercase text-gray-500">Countries, cheapest first</div>
            <div className="max-h-56 overflow-y-auto divide-y">
              {countries.filter(c => c.served).map(c => (
                <div key={c.iso2}
                     onMouseEnter={() => setListHover({ kind: 'country', iso2: c.iso2, label: c.country })}
                     onMouseLeave={() => setListHover(null)}
                     className="flex items-center justify-between px-3 py-1.5 text-sm cursor-default hover:bg-gray-50">
                  <span className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ background: rampAt(scale.t(c.cost)) }} />
                    {flag(c.iso2)} {c.country}
                  </span>
                  <span className="tabular-nums text-xs">€{c.cost.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-400 flex gap-2">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>
          Zones are drawn from their real postcodes, binned to ~11 km cells. Greece and Ireland have no open postcode
          coordinates, so their zones are listed but not painted.{' '}
          {data?.attribution || 'Postcode coordinates © GeoNames (CC BY 4.0), geonames.org'}. Country outlines Natural Earth.
        </span>
      </p>
    </div>
  );
}
